import {
  FC, memo, useState, useCallback, useMemo,
} from 'react';
import { Text } from 'shared/ui';
import dayjs from 'dayjs';
import { useAppSelector } from 'app/providers/Store';
import { engineMeasurementSelectors } from 'entities/engineMeasurement/model/selectors/engineMeasurementSelectors';
import { EngineDetail } from 'entities/engine/model/types/types';
import { OrderRequestDto } from 'shared/api';
import { OrderForm } from 'pages/Orders/components/OrderForm';
import { useOrders } from 'pages/Orders/hooks/useOrders';
import { useEngines } from 'pages/Engines/hooks/useEngines';
import { EngineForm } from 'pages/Engines/components/EngineForm/EngineForm';
import { EngineInspectionTimeline } from 'pages/Engines/components/EngineDetailModal/EngineInspectionTimeline';
import styles from './EngineMainInfoSection.module.scss';

interface EngineMainInfoSectionProps {
  engineDetail: EngineDetail;
}

const NOT_SPECIFIED = 'Не указано';

export const EngineMainInfoSection: FC<EngineMainInfoSectionProps> = memo(({
  engineDetail,
}) => {
  const { createNewOrder } = useOrders();
  const { loadEngineDetail } = useEngines();
  const [isOrderFormOpen, setIsOrderFormOpen] = useState(false);
  const [isEditFormOpen, setIsEditFormOpen] = useState(false);
  const engineMeasurements = useAppSelector(engineMeasurementSelectors.getAllEngineMeasurements);

  // ТО делают во время останова, поэтому отсчёт ведём до планируемого останова.
  // Расчётная дата следующего ТО - запасной вариант, она предполагает работу 24/7.
  const weeksUntilMaintenanceLabel = useMemo(() => {
    const target = engineDetail.plannedDateOfStop || engineDetail.nextMaintenance;
    if (!target) return NOT_SPECIFIED;

    const weeks = Math.floor(dayjs(target).diff(dayjs(), 'day') / 7);
    return weeks >= 0 ? String(weeks) : NOT_SPECIFIED;
  }, [engineDetail.plannedDateOfStop, engineDetail.nextMaintenance]);

  // Наработка для шкалы инспекций - из последнего измерения по моточасам.
  // Эквивалентные часы указываются не всегда, тогда берём наработку как есть.
  const currentInspectionHours = useMemo(() => {
    if (!engineMeasurements?.length) return undefined;
    const latest = [...engineMeasurements].sort((a, b) => a.engineHours - b.engineHours).at(-1);
    return latest?.equivalentHours ?? latest?.engineHours;
  }, [engineMeasurements]);

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'Не указано';
    return dayjs(dateString).format('DD.MM.YYYY');
  };

  const getEngineTypeLabel = (engineType: string) => {
    const typeLabels: Record<string, string> = {
      ELECTRIC: 'Генератор',
      HYBRID: 'Двигатель',
    };
    return typeLabels[engineType] || engineType;
  };

  const onSubmitOrder = (data: OrderRequestDto) => {
    createNewOrder({
      data: {
        ...data,
        status: OrderRequestDto.status.NEW,
        engineId: engineDetail.id || 0,
      },
      onSuccess: () => {
        if (engineDetail.id) {
          loadEngineDetail(engineDetail.id);
        }
      },
    });
    setIsOrderFormOpen(false);
  };

  const handleEditSuccess = useCallback(async () => {
    if (engineDetail.id) {
      await loadEngineDetail(engineDetail.id);
    }
  }, [engineDetail.id, loadEngineDetail]);

  return (
    <div className={styles.mainInfoSection}>
      <div className={styles.infoColumn}>
        <div className={styles.editRow}>
          <button
            type="button"
            className={styles.editButton}
            onClick={() => setIsEditFormOpen(true)}
            aria-label="Редактировать основную информацию"
            title="Редактировать основную информацию"
          >
            ✏️
          </button>
        </div>
        <div className={styles.infoItem}>
          <span className={styles.label}>Модель:</span>
          <span className={styles.value}>{engineDetail.orderCode || 'Не указана'}</span>
        </div>
        <div className={styles.infoItem}>
          <span className={styles.label}>Тип машины:</span>
          <span className={styles.value}>{getEngineTypeLabel(engineDetail.engineType || '')}</span>
        </div>
        <div className={styles.infoItem}>
          <span className={styles.label}>Серийный номер:</span>
          <span className={styles.value}>{engineDetail.serialNumber || 'Не указан'}</span>
        </div>
        <div className={styles.infoItem}>
          <span className={styles.label}>Комментарий:</span>
          <span className={styles.value}>{engineDetail.comment || 'Не указан'}</span>
        </div>
      </div>

      <div className={styles.maintenanceColumn}>
        <div className={styles.maintenanceCard}>
          <EngineInspectionTimeline
            currentHours={currentInspectionHours}
            intervalHours={engineDetail.maintenanceIntervalHours}
          />

          <div className={styles.maintenanceInfo}>
            <Text>
              До следующего ТО осталось
              {' '}
              <strong>{weeksUntilMaintenanceLabel}</strong>
              {weeksUntilMaintenanceLabel === NOT_SPECIFIED ? '.' : ' недель.'}
            </Text>
            <Text className={styles.warningText}>
              Сроки поставки запчастей могут достигать 50 недель.
            </Text>
            <a
              href="#"
              className={styles.requestLink}
              onClick={(e) => {
                e.preventDefault();
                setIsOrderFormOpen(true);
              }}
            >
              Сформировать запрос на материалы и работы
            </a>
            <div style={{ display: 'flex', gap: '36px' }}>
              <div className={styles.infoItem}>
                <span className={styles.label}>Последнее ТО:</span>
                <span className={styles.value}>{formatDate(engineDetail.lastMaintenance)}</span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.label}>Планируемый останов:</span>
                <span className={styles.value}>{formatDate(engineDetail.plannedDateOfStop)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      {isOrderFormOpen && (
        <OrderForm
          editingOrder={null}
          isEditing={false}
          onSubmit={onSubmitOrder}
          onCancel={() => setIsOrderFormOpen(false)}
        />
      )}
      {isEditFormOpen && (
        <EngineForm
          isOpen={isEditFormOpen}
          isEditing
          engineDetail={engineDetail}
          onSuccess={handleEditSuccess}
          onCancel={() => setIsEditFormOpen(false)}
        />
      )}
    </div>
  );
});

EngineMainInfoSection.displayName = 'EngineMainInfoSection';
